import React, { useState } from "react";
import { Link } from "react-router-dom";


import axios from 'axios';
import config from "../Common/Configurations/APIConfig";
import { rexTechTheme } from "../Common/Theme";
import { 
  Mail, 
  ArrowLeft,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

export default function ForgotPassword() {
    const [values, setValues] = useState({ email: "" });
    const [errors, setErrors] = useState({});
    const [btnSubmit, setBtnSubmit] = useState(false);
    const [showSuccessPage, setShowSuccessPage] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setValues((prev) => ({ ...prev, [name]: value }));
        if (errors.email === false) setErrors({});
    };

    const validate = () => {
        let temp = {};
        temp.email = values.email !== "" && /\S+@\S+\.\S+/.test(values.email);
        setErrors(temp);
        return temp.email;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validate()) {
            setBtnSubmit(true);
            const formData = {
                email: values.email,
                domainUrl: window.location.origin,
            };
            try {
                await axios.post(config.APIACTIVEURL+config.FORGOTPASSWORD, formData);
                setShowSuccessPage(true);
            } catch (error) {
                console.error("Reset failed:", error);
                setErrors({ submit: error?.response?.data?.message || "Failed to send reset link. Please try again." });
            } finally {
                setBtnSubmit(false);
            }
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: rexTechTheme.colors.neutral.lightGray,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: rexTechTheme.spacing.xl,
            fontFamily: rexTechTheme.typography.fontFamily
        }}>
            <div style={{
                width: '100%',
                maxWidth: '450px',
                backgroundColor: rexTechTheme.colors.neutral.white,
                padding: rexTechTheme.spacing.xxl,
                borderRadius: rexTechTheme.borderRadius.xlarge,
                boxShadow: rexTechTheme.shadows.medium,
                '@media (max-width: 768px)': {
                    padding: rexTechTheme.spacing.xl
                }
            }}>
                
                {/* Conditionally render the form or the success message */}
                {showSuccessPage ? (
                    <div style={{ textAlign: 'center' }}>
                        <div style={{
                            width: '80px',
                            height: '80px',
                            backgroundColor: rexTechTheme.colors.primary.lightest,
                            borderRadius: rexTechTheme.borderRadius.round,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 20px',
                            border: `2px solid ${rexTechTheme.colors.primary.light}`
                        }}>
                            <CheckCircle size={36} color={rexTechTheme.colors.primary.main} />
                        </div>
                        <h2 style={{
                            margin: `0 0 ${rexTechTheme.spacing.sm} 0`,
                            fontSize: rexTechTheme.typography.fontSize.large,
                            color: rexTechTheme.colors.primary.dark,
                            fontWeight: 600
                        }}>
                            Check Your Email
                        </h2>
                        <p style={{
                            margin: `0 0 ${rexTechTheme.spacing.lg} 0`,
                            color: rexTechTheme.colors.neutral.darkGray,
                            fontSize: rexTechTheme.typography.fontSize.regular
                        }}>
                            We've sent a password reset link to <strong>{values.email}</strong>.
                        </p>
                        <div style={{ marginTop: rexTechTheme.spacing.xl }}>
                            <Link 
                                to="/" 
                                style={{
                                    display: 'block',
                                    padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                                    backgroundColor: rexTechTheme.colors.primary.main,
                                    color: rexTechTheme.colors.neutral.white,
                                    borderRadius: rexTechTheme.borderRadius.medium,
                                    textDecoration: 'none',
                                    fontWeight: 600,
                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                    transition: 'all 0.3s ease',
                                    ':hover': {
                                        backgroundColor: rexTechTheme.colors.primary.dark,
                                        transform: 'translateY(-2px)',
                                        boxShadow: rexTechTheme.shadows.button
                                    }
                                }}
                            >
                                Return to Sign In
                            </Link>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Header */}
                        <div style={{ textAlign: 'center', marginBottom: rexTechTheme.spacing.xl }}>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                backgroundColor: rexTechTheme.colors.primary.lightest,
                                borderRadius: rexTechTheme.borderRadius.round,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 16px',
                                border: `2px solid ${rexTechTheme.colors.primary.light}`
                            }}>
                                <Mail size={28} color={rexTechTheme.colors.primary.main} />
                            </div>
                            <h1 style={{
                                margin: `0 0 ${rexTechTheme.spacing.sm} 0`,
                                fontSize: rexTechTheme.typography.fontSize.xlarge,
                                color: rexTechTheme.colors.primary.dark,
                                fontWeight: 600
                            }}>
                                Forgot Password?
                            </h1>
                            <p style={{
                                margin: 0,
                                color: rexTechTheme.colors.neutral.darkGray,
                                fontSize: rexTechTheme.typography.fontSize.regular
                            }}>
                                Enter your email and we'll send you a reset link.
                            </p>
                        </div>

                        {/* Error Message */}
                        {errors.submit && (
                            <div style={{
                                padding: rexTechTheme.spacing.md,
                                backgroundColor: rexTechTheme.colors.accent.lightest,
                                border: `1px solid ${rexTechTheme.colors.accent.orange}`,
                                borderRadius: rexTechTheme.borderRadius.medium,
                                color: rexTechTheme.colors.accent.orange,
                                marginBottom: rexTechTheme.spacing.lg,
                                display: 'flex',
                                alignItems: 'center',
                                gap: rexTechTheme.spacing.sm
                            }}>
                                <AlertCircle size={18} />
                                <div>{errors.submit}</div>
                            </div>
                        )}

                        {/* Form */}
                        <form onSubmit={handleSubmit} autoComplete="off" noValidate>
                            <div style={{ marginBottom: rexTechTheme.spacing.lg }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: rexTechTheme.spacing.xs,
                                    color: rexTechTheme.colors.primary.dark,
                                    fontWeight: 600,
                                    fontSize: rexTechTheme.typography.fontSize.regular
                                }}>
                                    Email
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <Mail 
                                        size={20} 
                                        style={{
                                            position: 'absolute',
                                            left: rexTechTheme.spacing.md,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: rexTechTheme.colors.neutral.darkGray,
                                            pointerEvents: 'none'
                                        }}
                                    />
                                    <input
                                        className={`form-control form-control-lg ${errors.email === false ? 'is-invalid' : ''}`}
                                        name="email"
                                        type="text"
                                        value={values.email}
                                        onChange={handleInputChange}
                                        style={{
                                            width: '100%',
                                            padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} 48px`,
                                            border: `2px solid ${errors.email === false ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                                            borderRadius: rexTechTheme.borderRadius.medium,
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            transition: 'all 0.2s ease',
                                            outline: 'none',
                                            boxSizing: 'border-box',
                                            ':focus': {
                                                borderColor: rexTechTheme.colors.primary.main,
                                                boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                            }
                                        }}
                                        placeholder="Enter your registered email"
                                    />
                                </div>
                                {errors.email === false && (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: rexTechTheme.spacing.xs,
                                        marginTop: rexTechTheme.spacing.xs,
                                        color: rexTechTheme.colors.accent.orange,
                                        fontSize: rexTechTheme.typography.fontSize.small
                                    }}>
                                        <AlertCircle size={14} />
                                        <span>Please enter a valid email.</span>
                                    </div>
                                )}
                            </div>
                            
                            {/* Submit Button */}
                            <div style={{ marginTop: rexTechTheme.spacing.xl }}>
                                <button 
                                    type="submit" 
                                    style={{
                                        width: '100%',
                                        padding: rexTechTheme.spacing.lg,
                                        background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
                                        color: rexTechTheme.colors.neutral.white,
                                        border: 'none',
                                        borderRadius: rexTechTheme.borderRadius.medium,
                                        fontSize: rexTechTheme.typography.fontSize.regular,
                                        fontWeight: 600,
                                        cursor: btnSubmit ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.3s ease',
                                        opacity: btnSubmit ? 0.7 : 1,
                                        boxShadow: rexTechTheme.shadows.button,
                                        ':hover': {
                                            background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
                                            transform: btnSubmit ? 'none' : 'translateY(-2px)',
                                            boxShadow: btnSubmit ? rexTechTheme.shadows.button : rexTechTheme.shadows.medium
                                        }
                                    }}
                                    disabled={btnSubmit}
                                >
                                    {btnSubmit ? (
                                        <>
                                            <div style={{
                                                width: '20px',
                                                height: '20px',
                                                border: `2px solid ${rexTechTheme.colors.neutral.white}`,
                                                borderTopColor: 'transparent',
                                                borderRadius: '50%',
                                                animation: 'spin 1s linear infinite',
                                                display: 'inline-block',
                                                marginRight: '8px'
                                            }} />
                                            Sending Link...
                                        </>
                                    ) : (
                                        'Send Reset Link'
                                    )}
                                </button>
                            </div>
                            
                            {/* Back to Login Link */}
                            <div style={{
                                marginTop: rexTechTheme.spacing.xl,
                                textAlign: 'center'
                            }}>
                                <Link 
                                    to="/" 
                                    style={{
                                        color: rexTechTheme.colors.primary.main,
                                        textDecoration: 'none',
                                        fontSize: rexTechTheme.typography.fontSize.regular,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: rexTechTheme.spacing.xs,
                                        ':hover': {
                                            textDecoration: 'underline'
                                        }
                                    }}
                                >
                                    <ArrowLeft size={16} />
                                    Back to Sign In
                                </Link>
                            </div>
                        </form>
                    </>
                )}

                {/* Footer */}
                <div style={{
                    marginTop: rexTechTheme.spacing.xl,
                    paddingTop: rexTechTheme.spacing.lg,
                    borderTop: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                    textAlign: 'center',
                    color: rexTechTheme.colors.neutral.darkGray,
                    fontSize: rexTechTheme.typography.fontSize.small
                }}>
                    <p style={{ margin: 0 }}>
                        © 2024 RexTech. All rights reserved.
                    </p>
                </div>
            </div>
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}